import { supabase } from "@/integrations/supabase/client";
import type { PageFilters, PageFull } from "@/types/page";

const VIEW_NAME = "v_pages_full";
const UPSERT_FUNCTION = "upsert_page_with_seo";

function isViewMissingError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const message = "message" in error ? String((error as any).message) : "";
  const details = "details" in error ? String((error as any).details) : "";
  return (
    message.includes(VIEW_NAME) ||
    message.includes("schema cache") ||
    message.includes("does not exist") ||
    details.includes(VIEW_NAME)
  );
}

async function getMergedPages() {
  const [{ data: pagesData, error: pagesError }, { data: seoData, error: seoError }] = await Promise.all([
    supabase
      .from("pages")
      .select("id, route, name, is_published, updated_at")
      .order("route", { ascending: true }),
    supabase
      .from("page_seo")
      .select("page_id, title, description, slug, canonical_url, og_title, og_description, og_image_url, indexable, no_follow, structured_data_json"),
  ]);

  if (pagesError) throw pagesError;
  if (seoError) throw seoError;

  const seoByPageId = new Map<string, any>();
  (seoData ?? []).forEach(entry => {
    if (entry?.page_id) {
      seoByPageId.set(entry.page_id, entry);
    }
  });

  const merged = (pagesData ?? [])
    .map(page => {
      const seo = seoByPageId.get(page.id) ?? {};
      const result: PageFull = {
        id: page.id,
        route: page.route,
        name: page.name,
        is_published: page.is_published,
        updated_at: page.updated_at,
        title: seo.title ?? null,
        description: seo.description ?? null,
        seo_slug: seo.slug ?? null,
        canonical_url: seo.canonical_url ?? null,
        og_title: seo.og_title ?? null,
        og_description: seo.og_description ?? null,
        og_image_url: seo.og_image_url ?? null,
        indexable: seo.indexable ?? true,
        no_follow: seo.no_follow ?? false,
        structured_data_json: seo.structured_data_json ?? null,
      };
      return result;
    });

  return merged;
}

async function fetchPagesFallback(filters: PageFilters = {}) {
  const limit = Math.max(filters.limit ?? 20, 1);
  const offset = Math.max(filters.offset ?? 0, 0);

  const merged = await getMergedPages();

  const normalizedSearch = filters.search?.trim().toLowerCase();
  const filtered = merged.filter(page => {
    if (!normalizedSearch) return true;
    const haystack = `${page.route} ${page.name}`.toLowerCase();
    return haystack.includes(normalizedSearch);
  });

  const total = filtered.length;
  const sliced = filtered.slice(offset, offset + limit);

  return {
    pages: sliced,
    total,
  };
}

async function fetchPageByIdFallback(id: string) {
  const merged = await getMergedPages();
  return merged.find(page => page.id === id) ?? null;
}

export type UpsertPagePayload = {
  page: {
    id?: string;
    route: string;
    name: string;
    is_published: boolean;
  };
  seo: {
    title?: string | null;
    description?: string | null;
    slug: string;
    canonical_url?: string | null;
    og_title?: string | null;
    og_description?: string | null;
    og_image_url?: string | null;
    indexable?: boolean | null;
    no_follow?: boolean | null;
    structured_data_json?: Record<string, unknown> | null;
  };
};

export async function listPages(filters: PageFilters = {}) {
  const limit = Math.max(filters.limit ?? 20, 1);
  const offset = Math.max(filters.offset ?? 0, 0);

  let query = supabase
    .from(VIEW_NAME)
    .select("*", { count: "exact" })
    .order("route", { ascending: true });

  if (filters.search) {
    const value = filters.search.trim();
    if (value) {
      const escaped = value.replace(/%/g, "\\%").replace(/,/g, "\\,");
      query = query.or(
        `route.ilike.%${escaped}%,name.ilike.%${escaped}%`
      );
    }
  }

  const from = offset;
  const to = offset + limit - 1;

  const { data, error, count } = await query.range(from, to);

  if (error) {
    if (isViewMissingError(error)) {
      return fetchPagesFallback(filters);
    }
    throw new Error(error.message);
  }

  return {
    pages: data ?? [],
    total: count ?? 0,
  };
}

export async function listAllRoutes() {
  const { data, error } = await supabase
    .from(VIEW_NAME)
    .select("id, route, name")
    .order("route", { ascending: true });

  if (error) {
    if (isViewMissingError(error)) {
      const merged = await getMergedPages();
      return merged.map(page => ({ id: page.id, route: page.route, name: page.name }));
    }
    throw new Error(error.message);
  }

  return data ?? [];
}

export async function getPageById(id: string) {
  const { data, error } = await supabase
    .from(VIEW_NAME)
    .select("*")
    .eq("id", id)
    .maybeSingle();

  if (error) {
    if (isViewMissingError(error)) {
      return fetchPageByIdFallback(id);
    }
    throw new Error(error.message);
  }

  return data ?? null;
}

export async function upsertPage(payload: UpsertPagePayload) {
  const { page, seo } = payload;

  const { data, error } = await supabase.rpc(UPSERT_FUNCTION, {
    p_id: page.id ?? null,
    p_route: page.route,
    p_name: page.name,
    p_is_published: page.is_published,
    p_title: seo.title ?? null,
    p_description: seo.description ?? null,
    p_slug: seo.slug,
    p_canonical_url: seo.canonical_url ?? null,
    p_og_title: seo.og_title ?? null,
    p_og_description: seo.og_description ?? null,
    p_og_image_url: seo.og_image_url ?? null,
    p_indexable: seo.indexable ?? true,
    p_no_follow: seo.no_follow ?? false,
    p_structured_data_json: seo.structured_data_json ?? null,
  });

  if (error) {
    if ((error as any).code === "23505") {
      throw new Error(
        "Esta rota ou slug já está em uso. Revise os campos e tente novamente."
      );
    }

    throw new Error(error.message);
  }

  if (!data) {
    throw new Error("Não foi possível salvar a página. Tente novamente.");
  }

  return data;
}
